import type { AccessLevel } from '@lms/api-client/backend';
import { Eye, Minus, Shield, Users2, Wrench } from 'lucide-react';

// ---------------------------------------------------------------------------
// Level presentation (labels are `access_matrix.level_<level>` messages).
// Light fills keep the (dark) cell text readable; the
// icon colour carries the distinction between levels. Deliberately no
// `dark:` variants: in this app they follow the OS setting while the rest of
// the UI stays light, which put black text on near-black fills.
// ---------------------------------------------------------------------------
export const LEVELS: AccessLevel[] = ['none', 'view', 'groups', 'users', 'all'];

export const LEVEL_META: Record<
  AccessLevel,
  {
    cell: string;
    swatch: string;
    icon: string;
    Icon: typeof Eye;
  }
> = {
  none: {
    cell: 'bg-transparent',
    swatch: 'bg-muted border border-border',
    icon: 'text-muted-foreground',
    Icon: Minus,
  },
  view: {
    cell: 'bg-sky-100',
    swatch: 'bg-sky-200 border border-sky-400',
    icon: 'text-sky-700',
    Icon: Eye,
  },
  groups: {
    cell: 'bg-amber-100',
    swatch: 'bg-amber-200 border border-amber-400',
    icon: 'text-amber-700',
    Icon: Wrench,
  },
  users: {
    cell: 'bg-violet-100',
    swatch: 'bg-violet-200 border border-violet-400',
    icon: 'text-violet-700',
    Icon: Users2,
  },
  all: {
    cell: 'bg-emerald-100',
    swatch: 'bg-emerald-200 border border-emerald-400',
    icon: 'text-emerald-700',
    Icon: Shield,
  },
};

/** Flags of a level, for intersecting levels. */
const FLAGS: Record<AccessLevel, [boolean, boolean, boolean]> = {
  none: [false, false, false],
  view: [true, false, false],
  groups: [true, true, false],
  users: [true, false, true],
  all: [true, true, true],
};

/**
 * The access two levels have in common: view only if both grant it, and each
 * manage flag only if both do. E.g. groups ∧ users = view.
 */
export function commonLevel(a: AccessLevel, b: AccessLevel): AccessLevel {
  const [va, ga, ua] = FLAGS[a];
  const [vb, gb, ub] = FLAGS[b];
  if (!(va && vb)) return 'none';
  const g = ga && gb;
  const u = ua && ub;
  if (g && u) return 'all';
  if (u) return 'users';
  if (g) return 'groups';
  return 'view';
}

export type LevelFlags = { view: boolean; groups: boolean; users: boolean };

export function flagsOf(level: AccessLevel): LevelFlags {
  const [view, groups, users] = FLAGS[level];
  return { view, groups, users };
}

/** A manage flag implies view; without view there's no access at all. */
export function levelOf({ view, groups, users }: LevelFlags): AccessLevel {
  if (groups && users) return 'all';
  if (users) return 'users';
  if (groups) return 'groups';
  return view ? 'view' : 'none';
}
